import { useNavigate } from "react-router-dom";
import { ArrowLeft, CreditCard, Plus, Calendar, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { useAppMode } from "@/hooks/useAppMode";
import { useUserMemberships } from "@/hooks/useUserMemberships";
import { useMemberships } from "@/hooks/useMemberships";
import { format } from "date-fns";

const currencySymbol = (c?: string) => (c === "USD" ? "$" : c === "EUR" ? "€" : "£");

const AppMemberships = () => {
  const navigate = useNavigate();
  const { provider } = useAppMode();
  const brandColor = provider?.brand_primary_color || undefined;

  const { data: myMemberships = [] } = useUserMemberships(provider?.id);
  const { data: availableMemberships = [] } = useMemberships(provider?.id);

  // Filter available to ones the user doesn't already actively own
  const ownedIds = new Set(myMemberships.map((m) => m.membership.id));
  const purchasable = availableMemberships.filter((m) => !ownedIds.has(m.id));

  return (
    <MobileAppLayout title="Memberships">
      <div className="px-4 py-4 space-y-5">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2"
          onClick={() => navigate("/app/profile")}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>

        <h1 className="text-2xl font-bold tracking-tight">Memberships</h1>

        {/* Active */}
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
            Active
          </h2>
          {myMemberships.length === 0 ? (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                No active memberships.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {myMemberships.map((m) => {
                const isPack = m.membership.membership_type === "pack_of_sessions";
                const total = m.membership.session_count ?? null;
                const used = m.sessions_used ?? 0;
                const remaining = m.sessions_remaining ?? 0;
                const pct = isPack && total && total > 0 ? Math.min(100, (used / total) * 100) : 0;
                const sym = currencySymbol(m.membership.currency);
                return (
                  <Card key={m.id}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <div
                          className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                          style={{
                            backgroundColor: brandColor ? `${brandColor}20` : "hsl(var(--muted))",
                          }}
                        >
                          <CreditCard className="h-5 w-5" style={{ color: brandColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{m.membership.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {isPack ? "Session pack" : "Active subscription"}
                          </p>
                        </div>
                        <span className="text-sm font-semibold whitespace-nowrap">
                          {sym}{Number(m.membership.price).toFixed(2)}
                        </span>
                      </div>

                      {isPack && total !== null && (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Credits used</span>
                            <span className="font-medium">
                              {used} / {total}
                            </span>
                          </div>
                          <Progress value={pct} className="h-1.5" />
                          <p className="text-xs text-muted-foreground">
                            {remaining} {remaining === 1 ? "session" : "sessions"} remaining
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border/60 text-xs">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Calendar className="h-3.5 w-3.5" />
                          <div>
                            <p className="text-[10px] uppercase tracking-wide">Started</p>
                            <p className="text-foreground font-medium">
                              {format(new Date(m.purchase_date), "d MMM yyyy")}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          {m.expires_at ? (
                            <>
                              <Clock className="h-3.5 w-3.5" />
                              <div>
                                <p className="text-[10px] uppercase tracking-wide">Expires</p>
                                <p className="text-foreground font-medium">
                                  {format(new Date(m.expires_at), "d MMM yyyy")}
                                </p>
                              </div>
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              <div>
                                <p className="text-[10px] uppercase tracking-wide">Validity</p>
                                <p className="text-foreground font-medium">No expiry</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Available */}
        {purchasable.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
              Available to purchase
            </h2>
            <div className="space-y-2">
              {purchasable.map((m) => (
                <Card key={m.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="font-semibold">{m.name}</p>
                        {m.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {m.description}
                          </p>
                        )}
                      </div>
                      <span className="font-semibold whitespace-nowrap">
                        £{Number(m.price).toFixed(2)}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      className="w-full mt-2"
                      onClick={() =>
                        navigate(`/provider/${provider?.slug}?membership=${m.id}`)
                      }
                      style={brandColor ? { backgroundColor: brandColor } : undefined}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Get membership
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </MobileAppLayout>
  );
};

export default AppMemberships;
